import { keyframes } from "@emotion/react";
import { type Theme } from "@mui/material";
import { type SystemStyleObject } from "@mui/system";

const unifiedTablePaperBorderRadius = "12px";
const tableSortLabelTrailingPadding = 3;
const tableSortLabelIconInset = (theme: Theme) => `calc(100% - ${theme.spacing(3)})`;
const tableSkeletonSweep = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
`;

/** Opaque hover fill keeps sticky table cells visually continuous with their row. */
export const getOpaqueTableRowHoverColor = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#242B38" : "#F5F5F5";

/** Shared visual treatment for tables displayed inside a card or panel. */
export const unifiedTableSx: SystemStyleObject<Theme> = {
  // Collapsed table borders do not honor the header cells' corner radii.
  borderCollapse: "separate",
  borderSpacing: 0,
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#374151" : "#F3F4F6"), // Tailwind gray-700 / gray-100
    borderTop: "1px solid",
    borderBottom: "1px solid",
    borderColor: "divider",
  },
  "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
    borderInlineStart: "1px solid",
    borderColor: "divider",
    borderStartStartRadius: unifiedTablePaperBorderRadius,
  },
  "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
    borderInlineEnd: "1px solid",
    borderColor: "divider",
    borderStartEndRadius: unifiedTablePaperBorderRadius,
  },
  "& .MuiTableCell-root": {
    fontSize: (theme) => theme.typography.body2.fontSize,
  },
  "& .MuiTableHead-root .MuiTableCell-root:has(.MuiTableSortLabel-root) .MuiTableSortLabel-root": {
    paddingInlineEnd: (theme) => theme.spacing(tableSortLabelTrailingPadding),
  },
  "& .MuiTableHead-root .MuiTableCell-alignRight:has(.MuiTableSortLabel-root) .MuiTableSortLabel-root":
    {
      paddingInlineEnd: 0,
      paddingInlineStart: (theme) => theme.spacing(tableSortLabelTrailingPadding),
    },
  "& .MuiTableHead-root .MuiTableCell-root:has(.MuiTableSortLabel-root) .MuiTableSortLabel-icon": {
    insetInlineStart: tableSortLabelIconInset,
  },
  "& .MuiTableHead-root .MuiTableCell-alignRight:has(.MuiTableSortLabel-root) .MuiTableSortLabel-icon":
    {
      insetInlineEnd: tableSortLabelIconInset,
      insetInlineStart: "auto",
    },
  "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
    backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#111827" : "#FFFFFF"), // Tailwind gray-900 / white
  },
  "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
    backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#1F2937" : "#F9FAFB"), // Tailwind gray-800 / gray-50
  },
  "&& .MuiTableBody-root .MuiTableRow-root:not(.table-empty-state):hover": {
    backgroundColor: getOpaqueTableRowHoverColor,
  },
  "&& .MuiTableBody-root .MuiTableRow-root:not(.table-empty-state):hover .MuiTableCell-root": {
    backgroundColor: getOpaqueTableRowHoverColor,
  },
  "& .MuiTableBody-root:has(.table-skeleton-row)": {
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      zIndex: 1,
      pointerEvents: "none",
      background:
        "/* @noflip */ linear-gradient(100deg, rgba(255, 255, 255, 0) 42%, rgba(255, 255, 255, 0.78) 50%, rgba(255, 255, 255, 0) 58%)",
      backgroundSize: "300% 100%",
      animation: `${tableSkeletonSweep} 3.3s ease-in-out infinite`,
      "@media (prefers-reduced-motion: reduce)": {
        display: "none",
      },
    },
  },
};

/** Type 2 tables are displayed within a card with an unframed, square header. */
export const tableInCardSx: SystemStyleObject<Theme> = {
  ...unifiedTableSx,
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#374151" : "#F3F4F6"),
    borderTop: 0,
    borderBottom: "1px solid",
    borderColor: "divider",
    whiteSpace: "normal",
  },
  "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
    borderInlineStart: 0,
    borderStartStartRadius: 0,
  },
  "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
    borderInlineEnd: 0,
    borderStartEndRadius: 0,
  },
  "& .MuiTableBody-root .MuiTableRow-root.table-empty-state:hover, & .MuiTableBody-root .MuiTableRow-root.table-empty-state:hover .MuiTableCell-root":
    {
      backgroundColor: "transparent",
    },
};
